import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import type { Group } from '@libs/src/models/group.model';
import { PathwayMembershipUnpopulated } from '@libs/src/models/pathway-membership.interface';
import type { PathwayUnpopulated } from '@libs/src/models/pathway.interface';
import type {
  V4DeletePathwayMembershipsQueryParams,
  V4GetPathwayMembershipsParams,
  V4GetPathwayMembershipsResponse,
  V4PatchPathwayMembershipsQueryParams,
} from '@libs/src/pathway-memberships/pathway-memberships.backend.types';

@Injectable({
  providedIn: 'root',
})
export class PathwayMembershipsBackend {
  v4 = {
    deleteGroupsPathwayMemberships: (
      groupId: Group['_id'],
      pathwayId: PathwayUnpopulated['_id'],
      params: V4DeletePathwayMembershipsQueryParams,
    ) =>
      this.http.delete<{ deletedCount: number }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships`,
        {
          params: {
            ...params,
          },
        },
      ),
    getPathwayMembershipToken: (token: string) =>
      this.http.get(`/api/v4/pathway-membership/${token}`),
    getPathwayMemberships: (
      groupId: Group['_id'],
      pathwayId: PathwayUnpopulated['_id'],
      params?: V4GetPathwayMembershipsParams,
    ) =>
      this.http.get<V4GetPathwayMembershipsResponse>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships`,
        {
          params: {
            ...params,
          },
        },
      ),
    patchGroupsPathwayMemberships: (
      groupId: Group['_id'],
      pathwayId: PathwayUnpopulated['_id'],
      params: V4PatchPathwayMembershipsQueryParams,
      body: Pick<PathwayMembershipUnpopulated, 'status'>,
    ) =>
      this.http.patch<{ modifiedCount: number }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships`,
        body,
        {
          params: {
            ...params,
          },
        },
      ),
    patchGroupsPathwaysMembership: (
      groupId: Group['_id'],
      pathwayId: PathwayUnpopulated['_id'],
      pathwayMembershipId: PathwayMembershipUnpopulated['_id'],
      body?: Pick<PathwayMembershipUnpopulated, 'account' | 'goals' | 'status'>,
    ) =>
      this.http.patch<{ pathwayMembership: PathwayMembershipUnpopulated }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships/${pathwayMembershipId}`,
        body,
      ),
    postPathwayMembershipsCsv: (
      groupId: Group['_id'],
      pathwayId: PathwayUnpopulated['_id'],
      params?: Omit<V4GetPathwayMembershipsParams, 'limit' | 'skip'>,
    ) =>
      this.http.post<{ message: string }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships/csv`,
        {
          params: {
            ...params,
          },
        },
      ),
    updatePathwayMembership: (
      groupId: Group['_id'],
      pathwayMembershipId: PathwayMembershipUnpopulated['_id'],
      body: Partial<PathwayMembershipUnpopulated> & {
        _id: NonNullable<PathwayMembershipUnpopulated['_id']>;
      },
    ) =>
      this.http.patch<{ pathwayMembership: PathwayMembershipUnpopulated }>(
        `/api/v4/groups/${groupId}/pathway-memberships/${pathwayMembershipId}`,
        body,
      ),
  };

  constructor(private readonly http: HttpClient) {}
}
